
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import PerformanceChartLegend from './PerformanceChartLegend';
import { getUtilityColor, getConsumptionLabel, getTooltipStyle } from '../utils/chartUtils';

interface PerformanceChartProps {
  chartData: any[];
  hiddenLines: string[];
  onLegendClick: (dataKey: string) => void;
  telemetryType: 'energy' | 'gas' | 'water';
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  chartData,
  hiddenLines,
  onLegendClick,
  telemetryType
}) => {
  // Custom legend data based on telemetry type
  const legendPayload = telemetryType === 'energy' 
    ? [
        { dataKey: 'averagePower', value: 'Average Power (W)', color: getUtilityColor(telemetryType) },
        { dataKey: 'dataTransferred', value: 'Data Transferred (MB)', color: '#3b82f6' },
        { dataKey: 'averageLatency', value: 'Average Latency (ms)', color: '#6b7280' }
      ]
    : [
        { dataKey: 'averageConsumption', value: getConsumptionLabel(telemetryType), color: getUtilityColor(telemetryType) },
        { dataKey: 'dataTransferred', value: 'Data Transferred (MB)', color: '#3b82f6' },
        { dataKey: 'averageLatency', value: 'Average Latency (ms)', color: '#6b7280' }
      ];

  // Get formatter for the tooltip based on data key
  const getFormatter = (value: any, name: string) => {
    if (name === 'averagePower') return [`${value} W`, 'Average Power'];
    if (name === 'averageConsumption') {
      if (telemetryType === 'gas') return [`${value} m³/h`, 'Consumption'];
      if (telemetryType === 'water') return [`${value} L/h`, 'Consumption'];
    }
    if (name === 'dataTransferred') return [`${value} MB`, 'Data Transferred'];
    if (name === 'averageLatency') return [`${value} ms`, 'Average Latency'];
    return [value, name];
  };

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis yAxisId="left" />
          <YAxis yAxisId="right" orientation="right" />
          <RechartsTooltip 
            contentStyle={getTooltipStyle()} 
            formatter={getFormatter}
          />
          
          {telemetryType === 'energy' ? (
            <Line 
              yAxisId="left" 
              type="monotone" 
              dataKey="averagePower" 
              name="Average Power (W)" 
              stroke={getUtilityColor(telemetryType)} 
              strokeWidth={2}
              hide={hiddenLines.includes('averagePower')}
            />
          ) : (
            <Line 
              yAxisId="left" 
              type="monotone" 
              dataKey="averageConsumption" 
              name={getConsumptionLabel(telemetryType)} 
              stroke={getUtilityColor(telemetryType)} 
              strokeWidth={2}
              hide={hiddenLines.includes('averageConsumption')}
            />
          )}
          
          <Line 
            yAxisId="right" 
            type="monotone" 
            dataKey="dataTransferred" 
            name="Data Transferred (MB)" 
            stroke="#3b82f6" 
            hide={hiddenLines.includes('dataTransferred')}
          />
          
          <Line 
            yAxisId="right" 
            type="monotone" 
            dataKey="averageLatency" 
            name="Average Latency (ms)" 
            stroke="#6b7280" 
            hide={hiddenLines.includes('averageLatency')}
          />
        </LineChart>
      </ResponsiveContainer>
      
      <PerformanceChartLegend 
        payload={legendPayload} 
        hiddenLines={hiddenLines} 
        onLegendClick={onLegendClick} 
      />
    </div>
  );
};

export default PerformanceChart;
